import {
  Button,
  ExportButton,
  Icons,
  List,
  Space,
  Table,
  TableProps,
  Typography,
  notification,
} from '@pankod/refine-antd';
import { IResourceComponentsProps, useCan, useNavigation } from '@pankod/refine-core';
import { CustomDatePicker, Search } from 'components';
import {
  IPaymentFormValues,
  MakePaymentWithReceipt,
} from 'components/payment/MakePaymentWithReceipt';
import { IAccount } from 'interfaces';
import { useEffect, useState } from 'react';
import { getAccounts } from 'services/account';
import { makePayment } from 'services/payment';
import { downloadCSVFile } from 'utilities/file';
import MetaData from 'utilities/metadata';
import { formatCurrency } from 'utilities/shared';
import { EUserType } from './logs.type';
import styles from './style.module.css';

interface IOptionGroup {
  value: string;
  label: string | React.ReactNode;
}

type SearchKeywords = {
  name: string[];
  type: string[];
};

interface ISearchObject {
  query?: string;
  userType?: EUserType;
}

const { Text } = Typography;

export const AccountList: React.FC<IResourceComponentsProps> = () => {
  const [accounts, setAccounts] = useState<IAccount[]>();
  const [keywords, setKeywords] = useState<string>('{}');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [visible, setVisible] = useState<boolean>(false);
  const [isPaymentLoading, setIsPaymentLoading] = useState<boolean>(false);
  const [accountInfo, setAccountInfo] = useState<IAccount>();
  const [isExportButtonLoading, setIsExportButtonLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [dateRange, setDateRange] = useState<[Date, Date]>(() => {
    const year = new Date().getFullYear();
    return [
      new Date(year, 0, 1, 0, 0, 0),
      new Date(year, 11, 31, 23, 59, 59)
    ];
  });

  const fetchDataOnMount = async (values?: [Date, Date]) => {
    setIsLoading(true);
    try {
      const rangeToUse = values || dateRange;
      if (values) {
        setDateRange(values);
      }
      const accountsResponse = await getAccounts({ startDate: rangeToUse[0].toISOString(), endDate: rangeToUse[1].toISOString() });
      const accountsData = accountsResponse.data;
      const keywordsData = accountsResponse.keywords;
      setAccounts(accountsData);
      setKeywords(keywordsData);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchDataOnMount();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { push } = useNavigation();

  const { data: canAccessShow } = useCan({
    resource: 'accounts',
    action: 'show',
    params: { field: 'id' },
  });

  const showDrawer = () => {
    setVisible(true);
  };

  const closeDrawer = () => {
    setVisible(false);
  };

  const renderTitle = (title: string) => (
    <div style={{ fontWeight: 'bold' }}>
      <Text style={{ fontSize: '1rem' }}>{title}</Text>
    </div>
  );

  const renderItem = (id: string, title: string) => ({
    value: title,
    label: (
      <div key={id}>
        <Text style={{ marginLeft: '1rem' }}>{title}</Text>
      </div>
    ),
  });

  const getOptionGroup = (options: string[]) => {
    const optionGroup: IOptionGroup[] = [];

    for (let [idx, value] of options.entries()) {
      if (!value) {
        continue;
      }
      optionGroup.push(renderItem(`${idx}`, value));
    }

    return optionGroup;
  };

  const getOptions = () => {
    const parsedKeywords: SearchKeywords = JSON.parse(keywords);
    let nameOptionGroup: IOptionGroup[] = [];
    let typeOptionGroup: IOptionGroup[] = ['Supplier', 'Client'].map((item, idx) =>
      renderItem(`${idx}`, item)
    );

    if (Object.keys(parsedKeywords).length) {
      nameOptionGroup = getOptionGroup(parsedKeywords.name);
    }

    return [
      {
        label: renderTitle('Name'),
        options: nameOptionGroup,
      },
      {
        label: renderTitle('Type'),
        options: typeOptionGroup,
      },
    ];
  };

  const onSelectOption = async (searchText: string) => {
    setIsLoading(true);
    try {
      const searchObject: ISearchObject = { query: undefined, userType: undefined };
      const queryTexts: string[] = [];
      for (let text of searchText.split(', ')) {
        if (EUserType.Client === text || EUserType.Supplier === text) {
          searchObject.userType = text;
        } else {
          if (text) queryTexts.push(text);
        }
      }
      if (queryTexts.length) searchObject.query = queryTexts.join(', ');

      const accountsResponse = await getAccounts(searchObject);
      const accountsData = accountsResponse.data;
      const keywordsData = accountsResponse.keywords;
      setAccounts(accountsData);
      setKeywords(keywordsData);
    } catch (err) {
      if (err instanceof Error) {
        notification.error({
          message: 'Failed',
          description: err.message,
        });
      }
    }
    setIsLoading(false);
  };

  const onExportButtonClick = async () => {
    setIsExportButtonLoading(true);
    try {
      const data = await getAccounts();
      const allAccountsData = data.data;
      const rows = [['Name', 'Type', 'Total Receivable', 'Total Payable']];
      allAccountsData.forEach((element) => {
        rows.push([
          element.name,
          element.type,
          element.totalReceivable.toString(),
          element.totalPayable.toString(),
        ]);
      });
      downloadCSVFile(rows);
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to Export CSV File.',
      });
    }
    setIsExportButtonLoading(false);
  };

  const pageHeaders = (
    <ExportButton loading={isExportButtonLoading} onClick={onExportButtonClick} />
  );

  const onPaymentSubmit = async (data: IPaymentFormValues) => {
    try {
      setIsPaymentLoading(true);
      const paymentData = {
        userType: accountInfo!.type,
        id: accountInfo!.id,
        paymentType: data.paymentType,
        paymentMethod: data.paymentMethod,
        currentPaidAmount: data.currentPaidAmount,
        paymentDate: new Date(),
        paymentDetails: data.paymentDetails,
        attachments: data.attachments,
        companyBank: data.companyBank,
      };
      const ledgerPaymentId = await makePayment(paymentData);
      const accountsResponse = await getAccounts();
      const accountsData = accountsResponse.data;
      const keywordsData = accountsResponse.keywords;
      setAccounts(accountsData);
      setKeywords(keywordsData);
      return ledgerPaymentId;
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: 'Failed to make payment',
      });
    } finally {
      setIsPaymentLoading(false);
    }
  };

  let totalPayableAmount = 0;
  if (accountInfo && accountInfo.type.toLowerCase() === 'supplier') {
    totalPayableAmount = accountInfo.totalPayable;
  }
  if (accountInfo && accountInfo.type.toLowerCase() === 'client') {
    totalPayableAmount = accountInfo.totalReceivable;
  }

  const onTableChange: TableProps<IAccount>['onChange'] = async (
    pagination,
    _filters,
    _sorter,
    _extra
  ) => {
    const page = pagination.current ?? 1;
    const limit = pagination.pageSize ?? 10;
    setPage(page);
    setLimit(limit);
  };

  return (
    <List headerProps={{ extra: pageHeaders }}>
      <MetaData />
      <div className={styles['filter-container']}>
        <CustomDatePicker
          className={styles['date-picker']}
          value={dateRange}
          onChange={fetchDataOnMount}
        />
        <Search
          placeholder="Search by Name, Type"
          size="large"
          className={styles.search}
          options={getOptions()}
          onSelectOption={onSelectOption}
          onReset={fetchDataOnMount}
        />

      </div>
      <Table
        dataSource={accounts}
        loading={isLoading}
        rowKey="id"
        scroll={{ x: true }}
        pagination={{
          current: page,
          pageSize: limit,
          position: ['bottomCenter'],
        }}
        onChange={onTableChange}
        bordered
      >
        <Table.Column dataIndex="name" title="Name" />
        <Table.Column dataIndex="type" title="Type" />
        <Table.Column<IAccount>
          dataIndex="totalReceivable"
          title="Total Receivable"
          render={(_, record) => formatCurrency(record.totalReceivable)}
        />
        <Table.Column<IAccount>
          dataIndex="totalPayable"
          title="Total Payable"
          render={(_, record) => formatCurrency(record.totalPayable)}
        />
        <Table.Column<IAccount>
          title="Actions"
          dataIndex="actions"
          render={(_text, record): React.ReactNode => {
            const endpoint = `show/${record.type.toLowerCase()}/${record.id}`;
            const isDisabled =
              (record.type.toLowerCase() === 'supplier' && !record.totalPayable) ||
              (record.type.toLowerCase() === 'client' && !record.totalReceivable);
            return (
              <Space>
                <Button
                  size="small"
                  disabled={!canAccessShow?.can}
                  icon={<Icons.EyeOutlined />}
                  onClick={() => push(endpoint)}
                />
                <Button
                  size="small"
                  icon={<Icons.DollarOutlined />}
                  disabled={isDisabled}
                  onClick={() => {
                    setAccountInfo(record);
                    showDrawer();
                  }}
                />
              </Space>
            );
          }}
        />
      </Table>

      {accountInfo && (
        <MakePaymentWithReceipt
          paymentBy={accountInfo.type}
          paymentById={accountInfo.id}
          totalPayableAmount={totalPayableAmount}
          loading={isPaymentLoading}
          visible={visible}
          closeDrawer={closeDrawer}
          onPaymentSubmit={onPaymentSubmit}
        />
      )}
    </List>
  );
};
